import React from 'react';
import {StyleSheet, View} from 'react-native';

import {getSkeletonColors, STAGGER} from '../constants';
import {Bone} from '../primitives/Bone';
import {BoneText} from '../primitives/BoneText';

/**
 * Skeleton for the rewards card screen.
 * Mirrors: AccountRewardsHasRewardsScreen layout
 * Reference: app/screens/account/AccountRewardsScreen/index.tsx
 *
 * Layout:
 *   "Your rewards card" heading
 *   Body text (2 lines)
 *   Blue MemberCard (logo, rewards badge, barcode, card number, name, member since)
 *   "Add to Wallet" button
 *   "Your temporary rewards code" heading + description
 *   6 in-store code character boxes
 *   "Access your card" paragraph
 *   "OPT OUT" link
 */
export const RewardsCardSkeleton: React.FC = React.memo(() => (
  <View style={styles.container}>
    {/* "Your rewards card" heading */}
    <BoneText variant="h1" width="55%" animationDelay={STAGGER.delay(0)} />

    {/* Body text — "Congrats, Kerry! You are now a part..." */}
    <BoneText
      variant="body"
      width="95%"
      animationDelay={STAGGER.delay(1)}
      style={{marginTop: 12}}
    />
    <BoneText variant="body" width="70%" animationDelay={STAGGER.delay(2)} style={{marginTop: 4}} />

    {/* ── MemberCard (blue card) ── */}
    <View style={styles.card}>
      {/* Brand logo (e.g. "Bottlemart") */}
      <Bone
        width={120}
        height={28}
        borderRadius={4}
        animationDelay={STAGGER.delay(3)}
        style={{alignSelf: 'center'}}
      />

      {/* Rewards program badge (e.g. "more rewards" square icon) */}
      <Bone
        width={64}
        height={64}
        borderRadius={10}
        animationDelay={STAGGER.delay(4)}
        style={{alignSelf: 'center', marginTop: 16}}
      />

      {/* Barcode section (white background) */}
      <View style={styles.barcodeSection}>
        {/* Barcode */}
        <Bone
          width="70%"
          height={42}
          borderRadius={2}
          animationDelay={STAGGER.delay(5)}
          style={{alignSelf: 'center'}}
        />

        {/* "Card number" label + number value */}
        <View style={styles.cardNumberRow}>
          <BoneText variant="caption" width="25%" animationDelay={STAGGER.delay(6)} />
          <BoneText variant="body" width="30%" animationDelay={STAGGER.delay(7)} />
        </View>
      </View>

      {/* Member name (e.g. "Kerry Smith") */}
      <Bone
        width="40%"
        height={14}
        borderRadius={3}
        animationDelay={STAGGER.delay(8)}
        style={{marginTop: 12}}
      />
      {/* Member since (e.g. "Member since 15 Mar 2024") */}
      <Bone
        width="50%"
        height={10}
        borderRadius={2}
        animationDelay={STAGGER.delay(9)}
        style={{marginTop: 6}}
      />
    </View>

    {/* ── Temporary rewards code section ── */}
    <BoneText variant="h1" width="65%" animationDelay={STAGGER.delay(11)} style={{marginTop: 50}} />
    <BoneText
      variant="body"
      width="80%"
      animationDelay={STAGGER.delay(12)}
      style={{marginTop: 8}}
    />

    {/* "Rewards in-store code" label */}
    <BoneText
      variant="caption"
      width="35%"
      animationDelay={STAGGER.delay(13)}
      style={{marginTop: 16}}
    />

    {/* 6 in-store code character boxes */}
    <View style={styles.codeBoxRow}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <View key={i} style={styles.codeBox}>
          <Bone width="100%" height={44} borderRadius={8} animationDelay={STAGGER.delay(14 + i)} />
        </View>
      ))}
    </View>

    {/* "Access your card" paragraph */}
    <BoneText
      variant="body"
      width="90%"
      animationDelay={STAGGER.delay(20)}
      style={{marginTop: 40}}
    />
    <BoneText
      variant="body"
      width="75%"
      animationDelay={STAGGER.delay(21)}
      style={{marginTop: 4}}
    />

    {/* "OPT OUT" link */}
    <Bone
      width={180}
      height={14}
      borderRadius={3}
      animationDelay={STAGGER.delay(22)}
      style={{alignSelf: 'center', marginTop: 50}}
    />
  </View>
));

RewardsCardSkeleton.displayName = 'RewardsCardSkeleton';

const skeletonColors = getSkeletonColors();

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  card: {
    borderRadius: 20,
    backgroundColor: '#D0D5DD',
    padding: 20,
    marginTop: 24,
    marginBottom: 8,
  },
  barcodeSection: {
    backgroundColor: skeletonColors.cardBackground,
    borderRadius: 8,
    padding: 12,
    marginTop: 20,
  },
  cardNumberRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  codeBoxRow: {
    flexDirection: 'row',
    marginTop: 12,
    gap: 8,
  },
  codeBox: {
    flex: 1,
  },
});
